import { useMemo, useState } from "react";
import { Box, CssBaseline, Divider, IconButton, List, ThemeProvider, Tooltip, Typography } from "@mui/material";
import { ArrowLeft, BarChart3, ClipboardList, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { DialogsProvider } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { bpmV2Theme } from "@themes/Bpm";
import { TopbarProfile } from "@components/v2/TopbarProfile";
import { SidebarGroupLabel, SidebarNavItem } from "@components/v2/SidebarNavItem";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";

interface ReportesSidebarItem {
    label: string;
    icon: React.ReactNode;
    segment: string;
    /** Código de módulo de permiso (mismos que usa `AdminLayout.tsx` para estas 2 páginas). */
    permissionCode: string;
}

const SIDEBAR_ITEMS: ReportesSidebarItem[] = [
    { label: "General", icon: <BarChart3 size={16} />, segment: "", permissionCode: "SBREGRAL" },
    { label: "Solicitudes", icon: <ClipboardList size={16} />, segment: "solicitudes", permissionCode: "SBRESOL" },
];

/**
 * Shell de `/v2/reportes` — mismo patrón de topbar+sidebar que `SolicitudLayoutV2.tsx`, pero
 * standalone (sin contexto de solicitud/compañía). El gating de items por permiso replica
 * `AdminLayout.tsx` (`permissions.find(p => p.module.code === "SBREGRAL"/"SBRESOL")`), mismos
 * códigos de módulo que ya usa v1 para estas 2 páginas.
 */
export const ReportesLayoutV2 = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [collapsed, setCollapsed] = useState(false);

    useTokenExpirationGuard();

    const userSession = useSelector((state: RootState) => state.session.user);
    const permissions = useSelector((state: RootState) => state.session.permissions);

    const initials = `${userSession.name?.charAt(0) ?? ""}${userSession.last_name?.charAt(0) ?? ""}`.toUpperCase() || "??";

    const items = useMemo(
        () => SIDEBAR_ITEMS.filter((item) => permissions.some((p) => p.module.code === item.permissionCode)),
        [permissions],
    );

    const isItemActive = (item: ReportesSidebarItem) =>
        location.pathname === `/v2/reportes/${item.segment}`.replace(/\/$/, "");

    return (
        <ThemeProvider theme={bpmV2Theme}>
            <CssBaseline />
            <DialogsProvider>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2.5,
                            flexShrink: 0,
                            height: 64,
                            px: 3,
                            backgroundColor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Tooltip title="Volver">
                            <IconButton onClick={() => navigate("/v2/solicitudes")} sx={{ color: "primary.main" }}>
                                <ArrowLeft size={18} />
                            </IconButton>
                        </Tooltip>
                        <Typography sx={{ typography: "h5", color: "text.primary", flexShrink: 0 }}>Lendia</Typography>
                        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
                        <Typography sx={{ typography: "h6", color: "text.primary" }}>Reportes</Typography>
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
                            <TopbarProfile initials={initials} withAccountMenu />
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                        <Box
                            sx={{
                                width: collapsed ? 64 : 220,
                                flexShrink: 0,
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "background.paper",
                                borderRight: "1px solid",
                                borderColor: "divider",
                                overflowY: "auto",
                                transition: "width 140ms ease",
                                py: 1,
                            }}
                        >
                            {!collapsed && <SidebarGroupLabel>Reportes</SidebarGroupLabel>}
                            <List disablePadding>
                                {items.map((item) => (
                                    <SidebarNavItem
                                        key={item.label}
                                        icon={item.icon}
                                        label={collapsed ? "" : item.label}
                                        selected={isItemActive(item)}
                                        onClick={() => navigate(`/v2/reportes/${item.segment}`.replace(/\/$/, ""))}
                                    />
                                ))}
                            </List>

                            <Box
                                sx={{
                                    mt: "auto",
                                    px: collapsed ? 0 : 2,
                                    pt: 1.5,
                                    display: "flex",
                                    justifyContent: collapsed ? "center" : "flex-start",
                                }}
                            >
                                <Tooltip title={collapsed ? "Expandir menú" : "Colapsar menú"}>
                                    <IconButton
                                        onClick={() => setCollapsed((prev) => !prev)}
                                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, gap: 1, px: collapsed ? 1 : 1.5, height: 32 }}
                                    >
                                        {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
                                        {!collapsed && <Typography sx={{ typography: "button", fontSize: "0.6875rem" }}>Colapsar</Typography>}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", px: 3, py: 2 }}>
                            <Outlet />
                        </Box>
                    </Box>
                </Box>
            </DialogsProvider>
        </ThemeProvider>
    );
};
